import { api } from "./api";
import {
  DocumentListFilters,
  DocumentListResponse,
  DocumentFacets,
  ShareLinkResponse,
  BulkActionResponse,
} from "@/types/document";

export const documentApi = {
  listDocuments: async (
    filters: DocumentListFilters,
    page: number = 1,
    pageSize: number = 25,
    sortBy: string = "created_at",
    sortOrder: "asc" | "desc" = "desc"
  ): Promise<DocumentListResponse> => {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
      sort_by: sortBy,
      sort_order: sortOrder,
    });

    if (filters.search) params.append("search", filters.search);
    if (filters.category) params.append("category", filters.category);
    if (filters.file_type) params.append("file_type", filters.file_type);
    if (filters.date_from) params.append("date_from", filters.date_from);
    if (filters.date_to) params.append("date_to", filters.date_to);
    if (filters.uploaded_by) params.append("uploaded_by", filters.uploaded_by);
    if (filters.folder_id) params.append("folder_id", filters.folder_id);
    // E14.3.2: FastAPI lê List[str] como `tags=a&tags=b` (não `tags[]=`).
    if (filters.tags) filters.tags.forEach((t) => params.append("tags", t));

    const url = "/documents/list?" + params.toString();
    const response = await api.get(url);
    return response.data;
  },

  // E14.3.2: facets pros filtros do Drive (tags + uploaders distintos da empresa).
  getFacets: async (): Promise<DocumentFacets> => {
    const response = await api.get("/documents/facets");
    return response.data;
  },

  // `disposition`: "attachment" (default no backend) força download de verdade;
  // "inline" é para preview in-app (react-pdf/img). Ver browser-download.ts.
  getDownloadUrl: async (
    documentId: string,
    disposition?: "attachment" | "inline"
  ): Promise<{ download_url: string }> => {
    const response = await api.get("/documents/" + documentId + "/download", {
      params: disposition ? { disposition } : undefined,
    });
    return response.data;
  },

  // Cria um documento Office EM BRANCO (Word/Excel/PowerPoint) no Drive e
  // retorna o Document criado (id/name/...) — o caller abre no editor OnlyOffice.
  createBlankDocument: async (
    docType: "docx" | "xlsx" | "pptx",
    name?: string,
    folderId?: string | null
  ): Promise<{ id: string; name: string; mime_type: string }> => {
    const response = await api.post("/documents/blank", {
      doc_type: docType,
      name: name || null,
      folder_id: folderId ?? null,
    });
    return response.data;
  },

  createShareLink: async (
    documentId: string,
    expiresInHours: number = 24
  ): Promise<ShareLinkResponse> => {
    const url = "/documents/" + documentId + "/share?expires_in_hours=" + expiresInHours;
    const response = await api.post(url);
    return response.data;
  },

  // `justificativa` vai no CORPO do DELETE, nunca na URL (log e histórico). Dentro da Biblioteca
  // da Qualidade é obrigatória (o administrador é dispensado); sem texto a chamada é a de sempre.
  deleteDocument: async (documentId: string, justificativa?: string): Promise<void> => {
    const url = "/documents/" + documentId;
    if (justificativa) {
      await api.delete(url, { data: { justificativa } });
    } else {
      await api.delete(url);
    }
  },

  // A justificativa vale para o lote inteiro e só vai no corpo quando existe.
  bulkDeleteDocuments: async (
    documentIds: string[],
    justificativa?: string
  ): Promise<BulkActionResponse> => {
    const response = await api.post("/documents/bulk-delete", {
      document_ids: documentIds,
      ...(justificativa ? { justificativa } : {}),
    });
    return response.data;
  },

  bulkMoveDocuments: async (
    documentIds: string[],
    targetFolderId?: string
  ): Promise<BulkActionResponse> => {
    const response = await api.post("/documents/bulk-move", {
      document_ids: documentIds,
      target_folder_id: targetFolderId,
    });
    return response.data;
  },

  bulkAddTags: async (documentIds: string[], tags: string[]): Promise<BulkActionResponse> => {
    const response = await api.post("/documents/bulk-add-tags", {
      document_ids: documentIds,
      tags,
    });
    return response.data;
  },

  getDashboard: async (): Promise<any> => {
    const response = await api.get("/documents/dashboard");
    return response.data;
  },

  toggleFavorite: async (documentId: string): Promise<{ is_favorite: boolean }> => {
    const response = await api.post(`/documents/${documentId}/favorite`);
    return response.data;
  },

  createFolder: async (folderData: {
    name: string;
    description?: string;
    parent_id?: string;
    color?: string;
    icon?: string;
  }): Promise<any> => {
    const response = await api.post("/documents/folders", folderData);
    return response.data;
  },

  searchDocuments: async (query: string, limit: number = 20, offset: number = 0): Promise<any> => {
    const params = new URLSearchParams({
      q: query,
      limit: limit.toString(),
      offset: offset.toString(),
    });
    const response = await api.get(`/documents/search?${params.toString()}`);
    return response.data;
  },
};
